import React, { useEffect, useRef, useState } from "react";
import {
  ArrowUpIcon,
  ArrowRightIcon,
  AdjustmentsHorizontalIcon,
  Bars3Icon,
  BuildingOffice2Icon,
  CalendarDaysIcon,
  BriefcaseIcon,
  ChatBubbleLeftRightIcon,
  CheckBadgeIcon,
  ClockIcon,
  Cog6ToothIcon,
  CodeBracketIcon,
  ComputerDesktopIcon,
  CommandLineIcon,
  CursorArrowRaysIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  ChevronDownIcon,
  HandRaisedIcon,
  HomeIcon,
  IdentificationIcon,
  LinkIcon,
  LockClosedIcon,
  MapPinIcon,
  PencilSquareIcon,
  PresentationChartLineIcon,
  PhoneIcon,
  ShieldCheckIcon,
  SparklesIcon,
  Squares2X2Icon,
  UserIcon,
  UserCircleIcon,
  UserGroupIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

type Service = {
  title: string;
  description: string;
  points: string[];
  outcome: string;
};

const services: Service[] = [
  {
    title: "Interim Management & Teamleitung",
    description:
      "Führung übernehmen, Entscheidungen treffen, Struktur schaffen.",
    points: [
      "Interim Lead für IT-, Engineering- und Produktteams",
      "Klare Priorisierung, Verantwortlichkeiten und Entscheidungswege",
      "Führung in Wachstums-, Veränderungs- oder Krisenphasen",
      "Hiring, Layoffs, Mentoring und Weiterentwicklung",
      "Aufbau selbstorganisierter Teams mit klaren Leitplanken",
    ],
    outcome: "Mehr Klarheit, bessere Entscheidungen und Teams, die wieder verlässlich liefern.",
  },
  {
    title: "IT-Beratung",
    description:
      "Technische Realität, Business-Ziele und Organisation zusammenbringen.",
    points: [
      "Sparring für CTO, CIO und Management",
      "Analyse von Systemlandschaften, Teams und Prozessen",
      "Make-or-Buy-, Vendor- und Tool-Entscheidungen",
      "Vermittlung zwischen Business, IT und externen Partnern",
      "Strukturierung komplexer Vorhaben in klare nächste Schritte",
    ],
    outcome: "Management und Teams arbeiten wieder an denselben Prioritäten.",
  },
  {
    title: "Software Architecture",
    description:
      "Architekturen, die Skalierung ermöglichen und Komplexität beherrschbar machen.",
    points: [
      "Zielarchitektur und technische Roadmaps",
      "Schnittstellen, Integrationen und Systemdesign",
      "Cloud-, E-Commerce-, ERP-, OMS-, WMS- und API-nahe Architekturen",
      "Bewertung bestehender Lösungen und technischer Schulden",
      "Architekturentscheidungen mit Blick auf Betrieb, Kosten und Wachstum",
    ],
    outcome: "Technische Entscheidungen werden nachvollziehbar, tragfähig und umsetzbar.",
  },
  {
    title: "Engineering & DevOps",
    description:
      "Strategie plus Umsetzung - nicht nur Folienberatung.",
    points: [
      "Hands-on Softwareentwicklung",
      "CI/CD, Cloud, Container, Kubernetes und AWS",
      "Verbesserung von Entwicklungsprozessen und Delivery-Pipelines",
      "Technische Umsetzung gemeinsam mit internen und externen Teams",
      "Stabilisierung von Plattformen, Prozessen und Betriebsmodellen",
    ],
    outcome: "Weniger Reibung zwischen Konzept, Entwicklung und Betrieb.",
  },
];

const customerLogos: { src: string; alt: string }[] = [
  { src: "/images/customers/Check24_slider.png", alt: "CHECK24" },
  { src: "/images/customers/dokuship_slider.png", alt: "Dokuship" },
  { src: "/images/customers/edeka_digital_slider.jpg", alt: "EDEKA Digital" },
  { src: "/images/customers/ESN_slider.png", alt: "ESN" },
  { src: "/images/customers/Fielmann_slider.png", alt: "Fielmann" },
  { src: "/images/customers/Funk_slider.png", alt: "Funk" },
  { src: "/images/customers/More_slider.png", alt: "More" },
  { src: "/images/customers/Quality_Group_slider.png", alt: "Quality Group" },
  { src: "/images/customers/Sportwagenhersteller_slider.png", alt: "Sportwagenhersteller" },
];

const serviceAnchorStatMeta: { label: string; icon: React.ElementType }[] = [
  { label: "Jahre Erfahrung", icon: UserCircleIcon },
  { label: "Abgeschlossene Projekte", icon: BriefcaseIcon },
  { label: "Projektstunden", icon: ClockIcon },
];

const COUNT_UP_DURATION_MS = 2200;

const serviceIcons = [UserGroupIcon, PresentationChartLineIcon, Squares2X2Icon, CommandLineIcon];

const targetGroups: string[] = [
  "Scale-ups mit wachsender Produkt- und Teamkomplexität",
  "E-Commerce-Unternehmen mit Integrations- und Plattformdruck",
  "Digitale Produktteams mit Delivery-Engpässen",
  "Mittelstand mit gewachsenen Systemlandschaften",
  "Organisationen im Umbau von IT-Struktur und Verantwortung",
  "Konzerne mit komplexen IT- und Stakeholder-Strukturen",
];

const targetGroupIcons = [
  UserGroupIcon,
  BriefcaseIcon,
  UserCircleIcon,
  Squares2X2Icon,
  CommandLineIcon,
  HomeIcon,
];

const workIconCircleStyle = { backgroundColor: "transparent", border: "1px solid rgba(79, 175, 63, 0.55)" };

const workPrinciples: { title: string; subtitle: string; icon: React.ElementType }[] = [
  { title: "Pragmatisch", subtitle: "statt theoretisch", icon: SparklesIcon },
  { title: "Hands-on", subtitle: "statt Distanzberatung", icon: HandRaisedIcon },
  { title: "Priorisiert", subtitle: "statt Aktionismus", icon: AdjustmentsHorizontalIcon },
  { title: "Nachhaltig", subtitle: "statt kurzfristig gefixt", icon: ShieldCheckIcon },
];

const experienceStats: { value: string; label: string; icon: React.ElementType }[] = [
  { value: "15+", label: "Jahre Erfahrung in IT & Digital", icon: BriefcaseIcon },
  { value: "25+", label: "Projekte in Produkt, Plattform & Delivery", icon: CursorArrowRaysIcon },
  { value: "180+", label: "Organisationen aufgebaut & skaliert, von 20 -> 180 FTE", icon: UserGroupIcon },
];

const experienceRoles: {
  step: string;
  title: string;
  summary: string;
  detail: string;
  result: string;
  icon: React.ElementType;
}[] = [
  {
    step: "01",
    title: "Interim CTO / Director Engineering",
    summary: "Führung übernehmen, wenn Orientierung und Tempo gebraucht werden.",
    detail:
      "Ich übernehme Verantwortung, baue und skaliere IT-Organisationen, schaffe klare Prioritäten und steuere Stakeholder, Budget und Delivery.",
    result: "Klare Entscheidungen, stabile Organisation, verlässliche Delivery.",
    icon: UserCircleIcon,
  },
  {
    step: "02",
    title: "IT-Beratung",
    summary: "Technische Komplexität sortieren und Entscheidungen belastbar machen.",
    detail:
      "Ich entwickle Zielarchitekturen, bewerte Optionen, begleite Make-or-Buy-Entscheidungen und erstelle Roadmaps, die Business und Technik verbinden.",
    result: "Klare Richtung, weniger technische Schulden, bessere Investitionen.",
    icon: Squares2X2Icon,
  },
  {
    step: "03",
    title: "Tech & Team Leadership",
    summary: "Teams befähigen, Verantwortung zu übernehmen und gemeinsam besser zu liefern.",
    detail:
      "Ich schaffe klare Rollen, bessere Prozesse und Entscheidungswege und coache Tech Leads und Teams in ihrer Entwicklung.",
    result: "Mehr Eigenverantwortung, weniger Reibung, bessere Zusammenarbeit.",
    icon: UserGroupIcon,
  },
  {
    step: "04",
    title: "Delivery & Umsetzung",
    summary: "Nicht nur beraten - auch ins Ergebnis bringen.",
    detail:
      "Ich unterstütze hands-on in Architektur, Engineering, Cloud, DevOps, CI/CD und Plattformen - von der Konzeption bis zum stabilen Betrieb.",
    result: "Konzepte, die umgesetzt werden und echten Geschäftswert liefern.",
    icon: CommandLineIcon,
  },
  {
    step: "05",
    title: "Produkt & Plattform",
    summary: "Erfahrung aus komplexen digitalen Ökosystemen und geschäftskritischen Plattformen.",
    detail:
      "Omnichannel, Checkout, Preise, technische Daten, Identity & Consent, Integrationen & APIs - von der Idee bis zum stabilen Betrieb.",
    result: "Pragmatische Lösungen für komplexe digitale Geschäftsprozesse.",
    icon: PresentationChartLineIcon,
  },
];

const experienceValueStrip: { text: string; icon: React.ElementType }[] = [
  { text: "Strategisch\nund pragmatisch", icon: ArrowRightIcon },
  { text: "Beraten\nund umsetzen", icon: CheckBadgeIcon },
  { text: "Auf Augenhöhe\nund verlässlich", icon: UserGroupIcon },
  { text: "Nachhaltige\nWirkung", icon: PresentationChartLineIcon },
];

const navigation: { label: string; href: string }[] = [
  { label: "Leistungen", href: "#leistungen" },
  { label: "Kunden", href: "#kunden" },
  { label: "Arbeitsweise", href: "#arbeitsweise" },
  { label: "Erfahrung", href: "#erfahrung" },
  { label: "Kontakt", href: "#kontakt" },
];

const contactActions: {
  info: string;
  action: string;
  href: string;
  icon: React.ElementType;
  style: string;
}[] = [
  {
    info: "Jetzt sprechen",
    action: "+49 172 422 55 45",
    href: "tel:+491724225545",
    icon: PhoneIcon,
    style: "bg-brand-500",
  },
  {
    info: "Fragen?",
    action: "Mail schicken",
    href: "mailto:sean.roddis@roddis-consulting.com",
    icon: EnvelopeIcon,
    style: "bg-brand-900",
  },
  {
    info: "Jetzt starten",
    action: "Termin vereinbaren",
    href: "https://calendly.com/sean-roddis/coffee-let-s-talk-about-the-challenges-you-re-facing?month=2026-05&date=2026-05-29",
    icon: ChatBubbleLeftRightIcon,
    style: "bg-brand-500",
  },
];

const processSteps: { step: string; title: string; text: string; icon: React.ElementType }[] = [
  {
    step: "01",
    title: "Verstehen",
    text: "Ziele, Engpässe und technische Realität transparent machen.",
    icon: ChatBubbleLeftRightIcon,
  },
  {
    step: "02",
    title: "Fokussieren",
    text: "Prioritäten setzen und auf die wirksamsten Hebel ausrichten.",
    icon: CursorArrowRaysIcon,
  },
  {
    step: "03",
    title: "Umsetzen",
    text: "Gemeinsam mit dem Team konkrete Verbesserungen liefern.",
    icon: UserGroupIcon,
  },
  {
    step: "04",
    title: "Verankern",
    text: "Wissen, Routinen und Verantwortung nachhaltig sichern.",
    icon: ShieldCheckIcon,
  },
];

const socialLinks: { label: string; href: string; icon: React.ReactNode }[] = [
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/in/sean-roddis-a81286126/",
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-current">
        <path d="M19 3A2 2 0 0 1 21 5V19A2 2 0 0 1 19 21H5A2 2 0 0 1 3 19V5A2 2 0 0 1 5 3H19M8.34 17V10.92H6.32V17H8.34M7.33 10.08A1.17 1.17 0 1 0 7.33 7.74A1.17 1.17 0 0 0 7.33 10.08M17.68 17V13.5C17.68 11.62 16.68 10.74 15.35 10.74C14.28 10.74 13.8 11.33 13.53 11.74V10.92H11.51V17H13.53V13.99C13.53 13.19 13.68 12.42 14.67 12.42C15.64 12.42 15.66 13.33 15.66 14.03V17H17.68Z" />
      </svg>
    ),
  },
  {
    label: "GitHub",
    href: "https://github.com/SRoddis",
    icon: (
      <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 fill-current">
        <path d="M12 .5A12 12 0 0 0 8.21 23.9C8.81 24 9.03 23.65 9.03 23.35V21.29C5.67 22.02 4.96 19.85 4.96 19.85C4.42 18.48 3.64 18.11 3.64 18.11C2.55 17.36 3.72 17.38 3.72 17.38C4.92 17.47 5.55 18.61 5.55 18.61C6.62 20.44 8.37 19.91 9.05 19.6C9.15 18.82 9.47 18.29 9.82 17.99C7.14 17.69 4.33 16.65 4.33 12.02C4.33 10.7 4.8 9.62 5.56 8.77C5.43 8.47 5.02 7.24 5.68 5.58C5.68 5.58 6.69 5.26 9.01 6.82C9.97 6.56 11.01 6.43 12.05 6.43C13.09 6.43 14.13 6.56 15.09 6.82C17.41 5.26 18.42 5.58 18.42 5.58C19.08 7.24 18.67 8.47 18.54 8.77C19.3 9.62 19.77 10.7 19.77 12.02C19.77 16.66 16.95 17.68 14.26 17.98C14.71 18.37 15.11 19.14 15.11 20.31V23.35C15.11 23.65 15.33 24 15.93 23.9A12 12 0 0 0 12 .5Z" />
      </svg>
    ),
  },
];

function App() {
  const currentYear = new Date().getFullYear();
  const getIsImpressumRoute = () => {
    const pathname = window.location.pathname.toLowerCase();
    const hash = window.location.hash.toLowerCase();
    return pathname.startsWith("/impressum") || hash.startsWith("#/impressum");
  };
  const [isImpressumPage, setIsImpressumPage] = useState(getIsImpressumRoute);
  const experienceYears = Math.max(new Date().getFullYear() - 2010, 0);
  const completedProjects = Math.round(experienceYears * 1.7);
  const projectHours = Math.round(experienceYears * 2546.5);
  const serviceAnchorTargetValues = [experienceYears, completedProjects, projectHours];
  const serviceAnchorStats = [
    {
      value: experienceYears,
      displayValue: String(experienceYears),
      ...serviceAnchorStatMeta[0],
    },
    {
      value: completedProjects,
      displayValue: String(completedProjects),
      ...serviceAnchorStatMeta[1],
    },
    {
      value: projectHours,
      displayValue: projectHours.toLocaleString("de-DE"),
      ...serviceAnchorStatMeta[2],
    },
  ];

  const navHrefs = ["#hero", ...navigation.map((item) => item.href)];
  const [navVisible, setNavVisible] = useState(false);
  const [navSmooth, setNavSmooth] = useState(false);
  const [mainMobileNavOpen, setMainMobileNavOpen] = useState(false);
  const [stickyMobileNavOpen, setStickyMobileNavOpen] = useState(false);
  const [openServiceIndex, setOpenServiceIndex] = useState(0);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [serviceContentHeights, setServiceContentHeights] = useState<number[]>([]);
  const [animatedStatValues, setAnimatedStatValues] = useState<number[]>(() =>
    serviceAnchorStats.map(() => 0),
  );
  const [activeHref, setActiveHref] = useState(
    window.location.hash && navHrefs.includes(window.location.hash)
      ? window.location.hash
      : "#hero",
  );
  const lastScrollY = useRef(0);
  const mainNavRef = useRef<HTMLElement | null>(null);
  const headerRef = useRef<HTMLElement | null>(null);
  const serviceStatsRef = useRef<HTMLDivElement | null>(null);
  const serviceContentRefs = useRef<Array<HTMLDivElement | null>>([]);
  const customerSliderRef = useRef<HTMLDivElement | null>(null);
  const statsAnimatedRef = useRef(false);

  useEffect(() => {
    const slider = customerSliderRef.current;
    if (!slider) {
      return;
    }

    let intervalId: number | undefined;
    let animationFrameId = 0;
    let isPaused = false;
    let isAnimating = false;

    const getStepSize = () => {
      const firstCard = slider.querySelector("article") as HTMLElement | null;
      if (!firstCard) {
        return 260;
      }

      const sliderStyles = window.getComputedStyle(slider);
      const gap = Number.parseFloat(sliderStyles.columnGap || sliderStyles.gap || "0") || 0;
      return firstCard.getBoundingClientRect().width + gap;
    };

    const stepSlide = () => {
      if (isPaused || isAnimating) {
        return;
      }

      const loopPoint = slider.scrollWidth / 2;
      const step = getStepSize();
      let startLeft = slider.scrollLeft;

      if (startLeft >= loopPoint - step * 0.5) {
        startLeft -= loopPoint;
        slider.scrollLeft = startLeft;
      }

      const durationMs = 1150;
      const animationStart = performance.now();
      isAnimating = true;

      const animate = (timestamp: number) => {
        const progress = Math.min((timestamp - animationStart) / durationMs, 1);
        const easedProgress = 1 - Math.pow(1 - progress, 3);
        slider.scrollLeft = startLeft + step * easedProgress;

        if (progress < 1) {
          animationFrameId = window.requestAnimationFrame(animate);
          return;
        }

        isAnimating = false;
      };

      animationFrameId = window.requestAnimationFrame(animate);
    };

    const pause = () => {
      isPaused = true;
    };

    const resume = () => {
      isPaused = false;
    };

    slider.addEventListener("mouseenter", pause);
    slider.addEventListener("mouseleave", resume);
    slider.addEventListener("touchstart", pause, { passive: true });
    slider.addEventListener("touchend", resume);

    intervalId = window.setInterval(stepSlide, 2600);

    return () => {
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
      }
      window.cancelAnimationFrame(animationFrameId);
      slider.removeEventListener("mouseenter", pause);
      slider.removeEventListener("mouseleave", resume);
      slider.removeEventListener("touchstart", pause);
      slider.removeEventListener("touchend", resume);
    };
  }, []);

  const scrollToTop = () => {
    window.history.replaceState(null, "", "#hero");
    window.scrollTo({ top: 0, behavior: "smooth" });
    setActiveHref("#hero");
  };

  const handleHomeClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setMainMobileNavOpen(false);
    setStickyMobileNavOpen(false);
    scrollToTop();
  };

  const handleBackFromImpressum = () => {
    window.location.href = "/#hero";
  };

  useEffect(() => {
    const updateRouteState = () => {
      setIsImpressumPage(getIsImpressumRoute());
    };

    window.addEventListener("hashchange", updateRouteState);
    window.addEventListener("popstate", updateRouteState);

    return () => {
      window.removeEventListener("hashchange", updateRouteState);
      window.removeEventListener("popstate", updateRouteState);
    };
  }, []);

  useEffect(() => {
    if (!isImpressumPage) {
      return;
    }

    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [isImpressumPage]);

  useEffect(() => {
    const updateScrollProgress = () => {
      const y = window.scrollY;
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      const nextProgress = maxScroll > 0 ? Math.min(Math.max(y / maxScroll, 0), 1) : 0;

      setScrollProgress(nextProgress);
      // Button is only visible after the user actually starts scrolling from the top.
      setShowScrollTop(y > 2);
    };

    updateScrollProgress();
    window.addEventListener("scroll", updateScrollProgress, { passive: true });
    window.addEventListener("resize", updateScrollProgress);

    return () => {
      window.removeEventListener("scroll", updateScrollProgress);
      window.removeEventListener("resize", updateScrollProgress);
    };
  }, []);

  useEffect(() => {
    const handleHashChange = () => {
      const nextHash = window.location.hash;
      if (navHrefs.includes(nextHash)) {
        setActiveHref(nextHash);
      }
    };

    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  useEffect(() => {
    const getActiveFromScroll = () => {
      const y = window.scrollY;
      const activationOffset = 180;

      if (y < 80) {
        return "#hero";
      }

      let current = "#hero";
      for (const item of navigation) {
        const section = document.querySelector(item.href) as HTMLElement | null;
        if (!section) {
          continue;
        }
        if (section.offsetTop - activationOffset <= y) {
          current = item.href;
        }
      }

      const nearBottom = window.innerHeight + y >= document.documentElement.scrollHeight - 4;
      if (nearBottom) {
        return navigation[navigation.length - 1].href;
      }

      return current;
    };

    const updateActiveFromScroll = () => {
      const next = getActiveFromScroll();
      setActiveHref((prev) => (prev === next ? prev : next));
    };

    updateActiveFromScroll();
    window.addEventListener("scroll", updateActiveFromScroll, { passive: true });
    window.addEventListener("resize", updateActiveFromScroll);
    return () => {
      window.removeEventListener("scroll", updateActiveFromScroll);
      window.removeEventListener("resize", updateActiveFromScroll);
    };
  }, []);

  useEffect(() => {
    const updateHeaderHeight = () => {
      const nextHeight = headerRef.current?.getBoundingClientRect().height ?? 0;
      setHeaderHeight((prev) => (Math.abs(prev - nextHeight) < 1 ? prev : nextHeight));
    };

    updateHeaderHeight();

    const observer = new ResizeObserver(updateHeaderHeight);
    if (headerRef.current) {
      observer.observe(headerRef.current);
    }

    window.addEventListener("resize", updateHeaderHeight);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateHeaderHeight);
    };
  }, []);

  useEffect(() => {
    const updateServiceContentHeights = () => {
      setServiceContentHeights(
        services.map((_, index) => serviceContentRefs.current[index]?.scrollHeight ?? 0),
      );
    };

    updateServiceContentHeights();
    window.addEventListener("resize", updateServiceContentHeights);

    return () => window.removeEventListener("resize", updateServiceContentHeights);
  }, [openServiceIndex]);

  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      const smoothStartY = 260;
      const mainNavTopInViewport = mainNavRef.current?.getBoundingClientRect().top ?? -1;
      const scrollingUp = currentY < lastScrollY.current;

      if (stickyMobileNavOpen) {
        setNavSmooth(currentY > smoothStartY);
        setNavVisible(true);
        lastScrollY.current = currentY;
        return;
      }

      // Upper page area: no slide animation so sticky nav disappears instantly.
      setNavSmooth(currentY > smoothStartY);

      if (scrollingUp) {
        // Show while original nav is still above the viewport.
        // Hide exactly when original nav reaches top to create a seamless handoff.
        setNavVisible(mainNavTopInViewport < 0 && currentY > 0);
      } else {
        setNavVisible(false);
      }
      lastScrollY.current = currentY;
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [stickyMobileNavOpen]);

  useEffect(() => {
    const bannerElement = serviceStatsRef.current;
    if (!bannerElement || statsAnimatedRef.current) {
      return;
    }

    let animationFrame = 0;
    const targetValues = [experienceYears, completedProjects, projectHours];

    const animateStats = () => {
      const startTime = performance.now();

      const step = (timestamp: number) => {
        const progress = Math.min((timestamp - startTime) / COUNT_UP_DURATION_MS, 1);
        const easedProgress = 1 - Math.pow(1 - progress, 3);

        setAnimatedStatValues(
          targetValues.map((target) => Math.round(target * easedProgress)),
        );

        if (progress < 1) {
          animationFrame = window.requestAnimationFrame(step);
          return;
        }

        setAnimatedStatValues(targetValues);
      };

      animationFrame = window.requestAnimationFrame(step);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting || statsAnimatedRef.current) {
          return;
        }

        statsAnimatedRef.current = true;
        animateStats();
        observer.disconnect();
      },
      { threshold: 0.35 },
    );

    observer.observe(bannerElement);

    return () => {
      observer.disconnect();
      window.cancelAnimationFrame(animationFrame);
    };
  }, [experienceYears, completedProjects, projectHours]);

  if (isImpressumPage) {
    const impressumFacts: { label: string; text: string; icon: React.ElementType }[] = [
      {
        label: "Kontakt",
        text: "Sean Roddis\nD-25421 Pinneberg\n\nTelefon: 0172 / 422 55 45\nE-Mail: sean.roddis@roddis-consulting.com",
        icon: UserIcon,
      },
      { label: "Inhaber", text: "Sean Roddis", icon: BuildingOffice2Icon },
      { label: "Steuernummer", text: "31/072/04100", icon: DocumentTextIcon },
      { label: "Redaktion", text: "Sean Roddis", icon: PencilSquareIcon },
      { label: "Webdesign", text: "Sean Roddis", icon: ComputerDesktopIcon },
    ];

    const legalSections: { title: string; icon: React.ElementType; paragraphs: string[] }[] = [
      {
        title: "Haftung für Inhalte",
        icon: IdentificationIcon,
        paragraphs: [
          "Die Inhalte unserer Seiten wurden mit größter Sorgfalt erstellt. Für die Richtigkeit, Vollständigkeit und Aktualität der Inhalte können wir jedoch keine Gewähr übernehmen. Als Diensteanbieter sind wir gemäß § 7 Abs. 1 TMG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich.",
          "Nach §§ 8 bis 10 TMG sind wir als Diensteanbieter jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen.",
          "Verpflichtungen zur Entfernung oder Sperrung der Nutzung von Informationen nach den allgemeinen Gesetzen bleiben hiervon unberührt. Eine diesbezügliche Haftung ist jedoch erst ab dem Zeitpunkt der Kenntnis einer konkreten Rechtsverletzung möglich.",
          "Bei Bekanntwerden von entsprechenden Rechtsverletzungen werden wir diese Inhalte umgehend entfernen.",
        ],
      },
      {
        title: "Haftung für Links",
        icon: LinkIcon,
        paragraphs: [
          "Unser Angebot enthält Links zu externen Webseiten Dritter, auf deren Inhalte wir keinen Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen.",
          "Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich. Die verlinkten Seiten wurden zum Zeitpunkt der Verlinkung auf mögliche Rechtsverstöße überprüft.",
          "Rechtswidrige Inhalte waren zum Zeitpunkt der Verlinkung nicht erkennbar. Eine permanente inhaltliche Kontrolle der verlinkten Seiten ist jedoch ohne konkrete Anhaltspunkte einer Rechtsverletzung nicht zumutbar.",
          "Bei Bekanntwerden von Rechtsverletzungen werden wir derartige Links umgehend entfernen.",
        ],
      },
      {
        title: "Urheberrecht",
        icon: CheckBadgeIcon,
        paragraphs: [
          "Die durch den Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem deutschen Urheberrecht.",
          "Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers.",
          "Downloads und Kopien dieser Seite sind nur für den privaten, nicht kommerziellen Gebrauch gestattet.",
          "Soweit die Inhalte auf dieser Seite nicht vom Betreiber erstellt wurden, werden die Urheberrechte Dritter beachtet. Inhalte Dritter werden als solche gekennzeichnet.",
          "Sollten Sie trotzdem auf eine Urheberrechtsverletzung aufmerksam werden, bitten wir um einen entsprechenden Hinweis.",
          "Bei Bekanntwerden von Rechtsverletzungen werden wir derartige Inhalte umgehend entfernen.",
        ],
      },
      {
        title: "Datenschutz",
        icon: LockClosedIcon,
        paragraphs: [
          "Die Nutzung unserer Webseite ist in der Regel ohne Angabe personenbezogener Daten möglich.",
          "Soweit auf unseren Seiten personenbezogene Daten, beispielsweise Name, Anschrift oder E-Mail-Adressen, erhoben werden, erfolgt dies, soweit möglich, stets auf freiwilliger Basis.",
          "Diese Daten werden ohne Ihre ausdrückliche Zustimmung nicht an Dritte weitergegeben.",
          "Wir weisen darauf hin, dass die Datenübertragung im Internet, zum Beispiel bei der Kommunikation per E-Mail, Sicherheitslücken aufweisen kann.",
          "Ein lückenloser Schutz der Daten vor dem Zugriff durch Dritte ist nicht möglich.",
          "Der Nutzung von im Rahmen der Impressumspflicht veröffentlichten Kontaktdaten durch Dritte zur Übersendung von nicht ausdrücklich angeforderter Werbung und Informationsmaterialien wird hiermit ausdrücklich widersprochen.",
          "Die Betreiber der Seiten behalten sich ausdrücklich rechtliche Schritte im Falle der unverlangten Zusendung von Werbeinformationen, etwa durch Spam-Mails, vor.",
        ],
      },
    ];

    return (
      <div className="min-h-screen bg-[#f5f7f4] text-brand-900">
        <main>
          <section className="relative isolate overflow-hidden bg-[#050b16] text-brand-100">
            <img
              src="/images/impressum.png"
              alt=""
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 h-full w-full object-cover object-center"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#020712]/96 via-[#020712]/78 to-[#020712]/20" />

            <div className="relative mx-auto w-full max-w-7xl px-6 pb-28 pt-20 lg:px-10 lg:pb-32 lg:pt-24">
              <div className="flex justify-start">
                <button
                  type="button"
                  onClick={handleBackFromImpressum}
                  className="inline-flex items-center gap-2 rounded-full border border-white/35 bg-black/30 px-4 py-2 text-sm font-semibold text-white transition hover:border-brand-400 hover:text-brand-200"
                >
                  <ArrowRightIcon className="h-4 w-4 rotate-180" aria-hidden="true" />
                  Zurück
                </button>
              </div>

              <p className="mt-10 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.28em] text-brand-400">
                <span aria-hidden="true" className="h-0.5 w-10 rounded-full bg-brand-500" />
                Roddis Consulting
              </p>
              <h1 className="mt-4 font-display text-[clamp(2.7rem,8vw,5.3rem)] font-semibold leading-[0.96] text-white">Impressum</h1>
              <p className="mt-6 max-w-2xl text-lg leading-relaxed text-brand-100/88">
                Transparenz. Vertrauen. Verbindlichkeit.
                <br />
                Rechtliche Informationen gemäß § 5 TMG.
              </p>
            </div>
          </section>

          <section className="mx-auto w-full max-w-7xl px-6 pb-8 pt-10 lg:px-10 lg:pt-12">
            <div className="grid gap-0 md:grid-cols-2 xl:grid-cols-[1.55fr_1fr_1fr_1fr_1fr]">
              {impressumFacts.map((item, index) => {
                const Icon = item.icon;
                return (
                  <article
                    key={item.label}
                    className={`relative p-6 lg:p-7 ${
                      index < impressumFacts.length - 1
                        ? "xl:after:absolute xl:after:right-0 xl:after:top-1/2 xl:after:h-[72%] xl:after:w-px xl:after:-translate-y-1/2 xl:after:bg-brand-200/75"
                        : ""
                    }`}
                  >
                    <Icon className="h-7 w-7 text-brand-500" aria-hidden="true" />
                    <h2 className="mt-4 text-sm font-semibold uppercase tracking-[0.08em] text-brand-800">{item.label}</h2>
                    <p className="mt-3 whitespace-pre-line leading-relaxed text-[#666b72]">{item.text}</p>
                  </article>
                );
              })}
            </div>
          </section>

          <section className="mx-auto w-full max-w-7xl space-y-9 px-6 pb-14 pt-3 lg:px-10 lg:pb-16">
            {legalSections.map((section, index) => {
              const Icon = section.icon;
              return (
                <article key={section.title} className="grid grid-cols-[4.25rem_1fr] gap-4 lg:gap-6">
                  <div className="flex flex-col items-center">
                    <span className="inline-flex h-14 w-14 flex-none items-center justify-center rounded-full border border-brand-500/45 text-brand-500">
                      <Icon className="h-7 w-7" aria-hidden="true" />
                    </span>
                    {index < legalSections.length - 1 ? <span className="mt-3 h-full w-px bg-brand-200/80" /> : null}
                  </div>
                  <div className="border-l border-brand-200/80 pl-5 lg:pl-7">
                    <h2 className="text-[1.55rem] font-semibold uppercase tracking-[0.03em] leading-tight text-brand-700">
                      {section.title}
                    </h2>
                    <div className="mt-4 space-y-3 text-[15px] leading-relaxed text-brand-700/92">
                      {section.paragraphs.map((paragraph) => (
                        <p key={paragraph}>{paragraph}</p>
                      ))}
                    </div>
                  </div>
                </article>
              );
            })}
          </section>
        </main>

        <footer className="border-t border-white/10 bg-black text-brand-100">
          <div className="mx-auto grid w-full max-w-7xl gap-8 px-6 py-10 lg:grid-cols-[1fr_1.25fr_1.35fr] lg:px-10">
            <div className="lg:pr-8">
              <img
                src="/images/roddis_logo.svg"
                alt="Roddis Consulting Logo"
                className="h-14 w-auto object-contain"
              />
              <p className="mt-5 max-w-xs text-[18px] leading-snug text-brand-100 lg:text-[20px]">
                Roddis Consulting - Interim Leadership, Architekturberatung und Umsetzung für
                skalierende IT-Organisationen.
              </p>
              <div className="mt-7 flex gap-3">
                {socialLinks.map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={link.label}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-brand-500/50 bg-brand-900/40 text-xs font-bold uppercase tracking-wide text-brand-100 transition hover:border-brand-500 hover:text-brand-300"
                  >
                    {link.icon}
                  </a>
                ))}
              </div>
            </div>

            <div className="border-white/10 lg:border-l lg:pl-8">
              <span className="mb-4 block h-0.5 w-10 bg-brand-500" />
              <h3 className="text-xl font-bold uppercase tracking-tight text-white lg:text-2xl">Kontakt Info</h3>
              <div className="mt-4 space-y-2 text-base text-brand-200 lg:text-lg">
                <a
                  href="tel:+491724225545"
                  className="group flex items-center gap-3 border-b border-white/10 py-3 transition hover:text-brand-300"
                >
                  <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full border border-brand-500/50 bg-black/35">
                    <PhoneIcon className="h-5 w-5 text-brand-500" aria-hidden="true" />
                  </span>
                  <span className="relative inline-flex flex-col">
                    <span>+49 172 422 55 45</span>
                    <span className="mt-1 h-1 w-0 rounded-full bg-brand-500 transition-all duration-300 group-hover:w-full" />
                  </span>
                </a>
                <a
                  href="mailto:sean.roddis@roddis-consulting.com"
                  className="group flex items-center gap-3 border-b border-white/10 py-3 transition hover:text-brand-300"
                >
                  <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full border border-brand-500/50 bg-black/35">
                    <EnvelopeIcon className="h-5 w-5 text-brand-500" aria-hidden="true" />
                  </span>
                  <span className="relative inline-flex flex-col break-words">
                    <span>sean.roddis@roddis-consulting.com</span>
                    <span className="mt-1 h-1 w-0 rounded-full bg-brand-500 transition-all duration-300 group-hover:w-full" />
                  </span>
                </a>
                <div className="group flex items-center gap-3 py-3">
                  <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full border border-brand-500/50 bg-black/35">
                    <MapPinIcon className="h-5 w-5 text-brand-500" aria-hidden="true" />
                  </span>
                  <span className="relative inline-flex flex-col">
                    <span>Hamburg, Deutschland</span>
                    <span className="mt-1 h-1 w-0 rounded-full bg-brand-500 transition-all duration-300 group-hover:w-full" />
                  </span>
                </div>
              </div>
            </div>

            <div className="border-white/10 lg:border-l lg:pl-8">
              <span className="mb-4 block h-0.5 w-10 bg-brand-500" />
              <h3 className="text-xl font-bold uppercase tracking-tight text-white lg:text-2xl">Standort</h3>
              <div className="relative mt-4 w-full aspect-[297/159] overflow-hidden rounded-2xl border border-white/10 bg-brand-900">
                <img
                  src="/images/map.png"
                  alt="Standortkarte"
                  className="h-full w-full object-cover object-[50%_45%] opacity-85"
                />
                <div className="absolute left-[52.9%] top-[27.8%] -translate-x-1/2 -translate-y-1/2">
                  <span className="absolute inset-[-2px] h-5 w-5 animate-ping rounded-full bg-[#7CFC00]/95" />
                  <span className="absolute inset-[-7px] h-8 w-8 animate-pulse rounded-full border-2 border-[#B6FF4D]/95" />
                  <span className="relative block h-4 w-4 rounded-full bg-[#39FF14] shadow-[0_0_0_7px_rgba(57,255,20,0.48),0_0_26px_7px_rgba(124,252,0,0.72)]" />
                </div>
                <div className="absolute bottom-3 left-3 flex items-center gap-1.5 rounded-full bg-black/70 px-3 py-1 text-xs font-semibold text-brand-100 shadow-sm">
                  <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 text-brand-500 fill-current">
                    <path d="M12 2.5C8.7 2.5 6 5.2 6 8.5C6 13.4 12 21.5 12 21.5C12 21.5 18 13.4 18 8.5C18 5.2 15.3 2.5 12 2.5ZM12 11.1C10.6 11.1 9.4 9.9 9.4 8.5C9.4 7.1 10.6 5.9 12 5.9C13.4 5.9 14.6 7.1 14.6 8.5C14.6 9.9 13.4 11.1 12 11.1Z" />
                  </svg>
                  <span>Hamburg</span>
                </div>
              </div>
            </div>
          </div>
        </footer>

        <div className="border-t border-white/10 bg-brand-900/95 text-brand-100 backdrop-blur">
          <div className="mx-auto flex w-full max-w-7xl flex-col items-center gap-3 px-6 py-4 text-sm lg:px-10">
            <a
              href="/#/impressum"
              className="inline-flex w-fit items-center text-brand-300 transition hover:text-white"
            >
              Impressum
            </a>
            <p className="text-brand-200">Roddis Consulting © {currentYear}. All Rights Reserved</p>
          </div>
        </div>

        <button
          type="button"
          aria-label="Nach oben scrollen"
          onClick={scrollToTop}
          className={`group fixed bottom-[calc(env(safe-area-inset-bottom)+1.25rem)] right-[calc(env(safe-area-inset-right)+1.25rem)] z-[60] flex h-14 w-14 items-center justify-center rounded-full p-[3px] text-white shadow-lg shadow-black/35 transition-[transform,opacity] duration-300 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${
            showScrollTop ? "pointer-events-auto translate-y-0 opacity-100" : "pointer-events-none translate-y-8 opacity-0"
          }`}
        >
          <span
            className="pointer-events-none absolute inset-0 rounded-full"
            style={{
              background: `conic-gradient(rgba(79,175,63,0.96) ${scrollProgress * 360}deg, rgba(255,255,255,0.18) ${scrollProgress * 360}deg)`,
            }}
          />
          <span className="pointer-events-none absolute inset-[3px] rounded-full bg-brand-800 transition group-hover:bg-brand-700" />
          <ArrowUpIcon className="relative h-6 w-6" aria-hidden="true" />
        </button>
      </div>
    );
  }

  return (
    <div className="bg-brand-900 text-brand-50">
      {/* Sticky Nav – slides in from top when scrolling up */}
      <div
        className={`fixed left-0 right-0 top-0 z-50 border-b border-white/10 bg-brand-900/95 backdrop-blur ${
          navSmooth ? "transition-transform duration-150 ease-linear" : "transition-none"
        } ${
          navVisible ? "translate-y-0" : "-translate-y-full"
        }`}
      >
        <div className="mx-auto w-full max-w-6xl px-6 lg:px-10">
          <div className="flex items-center justify-between min-[870px]:hidden">
            <a
              href="#hero"
              aria-label="Nach oben"
              onClick={handleHomeClick}
              className={`group relative flex items-center justify-center px-1 py-5 transition ${
                activeHref === "#hero" ? "text-white" : "text-brand-100 hover:text-white"
              }`}
            >
              <HomeIcon className="h-5 w-5" aria-hidden="true" />
              <span
                className={`absolute inset-x-0 bottom-0 h-0.5 bg-brand-500 transition-opacity ${
                  activeHref === "#hero" ? "opacity-100" : "opacity-0 group-hover:opacity-70"
                }`}
              />
            </a>

            <button
              type="button"
              onClick={() => {
                setMainMobileNavOpen(false);
                setStickyMobileNavOpen((prev) => !prev);
              }}
              aria-expanded={stickyMobileNavOpen}
              aria-controls="sticky-mobile-nav"
              aria-label="Navigation umschalten"
              className="rounded-md border border-white/20 p-2 text-brand-100 transition hover:border-brand-500 hover:text-white"
            >
              {stickyMobileNavOpen ? <XMarkIcon className="h-5 w-5" /> : <Bars3Icon className="h-5 w-5" />}
            </button>
          </div>

          <div
            id="sticky-mobile-nav"
            className={`${stickyMobileNavOpen ? "grid" : "hidden"} gap-2 pb-4 pt-4 min-[870px]:hidden`}
          >
            {navigation.map((item, index) => (
              <a
                key={`sticky-mobile-${item.label}`}
                href={item.href}
                onClick={() => {
                  setActiveHref(item.href);
                  setStickyMobileNavOpen(false);
                }}
                className={`group relative rounded-md px-4 py-3 text-sm font-semibold uppercase tracking-[0.12em] transition ${
                  index === 0 ? "mt-2" : ""
                } ${
                  activeHref === item.href ? "text-white" : "text-brand-100 hover:text-white"
                }`}
              >
                {item.label}
                <span
                  className={`absolute bottom-0 left-3 h-0.5 w-1/2 bg-brand-500 transition-opacity ${
                    activeHref === item.href ? "opacity-100" : "opacity-0 group-hover:opacity-70"
                  }`}
                />
              </a>
            ))}
          </div>

          <div className="hidden items-end gap-8 overflow-x-auto pl-4 min-[870px]:flex">
            <a
              href="#hero"
              aria-label="Nach oben"
              onClick={handleHomeClick}
              className={`group relative flex items-center justify-center px-1 py-5 transition ${
                activeHref === "#hero" ? "text-white" : "text-brand-100 hover:text-white"
              }`}
            >
              <HomeIcon className="h-5 w-5" aria-hidden="true" />
              <span
                className={`absolute inset-x-0 bottom-0 h-0.5 bg-brand-500 transition-opacity ${
                  activeHref === "#hero" ? "opacity-100" : "opacity-0 group-hover:opacity-70"
                }`}
              />
            </a>
            {navigation.map((item) => (
              <a
                key={item.label}
                href={item.href}
                onClick={() => setActiveHref(item.href)}
                className={`group relative whitespace-nowrap px-1 py-5 text-sm font-semibold uppercase tracking-[0.12em] transition ${
                  activeHref === item.href ? "text-white" : "text-brand-100 hover:text-white"
                }`}
              >
                {item.label}
                <span
                  className={`absolute inset-x-0 bottom-0 h-0.5 bg-brand-500 transition-opacity ${
                    activeHref === item.href ? "opacity-100" : "opacity-0 group-hover:opacity-70"
                  }`}
                />
              </a>
            ))}
          </div>
        </div>
      </div>

      <header ref={headerRef} className="border-b border-white/10 bg-brand-900/95 backdrop-blur">
        <div className="bg-black">
          <div className="flex w-full items-stretch overflow-hidden">
            {/* Logo – erbt schwarz vom Parent */}
            <a
              href="#hero"
              onClick={handleHomeClick}
              className="ml-8 flex w-44 flex-none items-center justify-start py-1 pr-3 sm:ml-12 sm:w-52 sm:pr-4 lg:ml-24 lg:w-64"
            >
              <img
                src="/images/roddis_logo.svg"
                alt="Roddis Consulting Logo"
                className="h-16 w-auto object-contain sm:h-20 lg:h-28"
              />
            </a>

            {/* Parallelogramm-Segmente */}
            <div className="ml-auto flex min-w-0 flex-1 min-[870px]:w-[68%] min-[870px]:flex-none lg:w-[62%] xl:w-[58%]">
              {/* Mobile: nur "Jetzt sprechen" (erstes Element), rechtsbündig ohne schwarzen Rand */}
              <a
                href={contactActions[0].href}
                style={{ clipPath: "polygon(28px 0%, 100% 0%, 100% 100%, 0% 100%)" }}
                className={`relative flex flex-1 items-center justify-center gap-2 px-8 py-3 text-white transition hover:brightness-110 min-[870px]:hidden ${contactActions[0].style}`}
              >
                <PhoneIcon className="h-7 w-7 flex-none" aria-hidden="true" />
                <div className="flex flex-col">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] opacity-80">
                    {contactActions[0].info}
                  </p>
                  <p className="text-[13px] font-bold uppercase tracking-[0.1em]">
                    {contactActions[0].action}
                  </p>
                </div>
              </a>

              {/* >=640px: alle drei Segmente */}
              {contactActions.map((item, i) => {
                const Icon = item.icon;
                const isLast = i === contactActions.length - 1;
                const opensInNewTab = item.href.startsWith("http");
                const clipPath = isLast
                  ? "polygon(40px 0%, 100% 0%, 100% 100%, 0% 100%)"
                  : "polygon(40px 0%, calc(100% + 1px) 0%, calc(100% - 39px) 100%, 0% 100%)";
                return (
                  <a
                    key={item.action}
                    href={item.href}
                    target={opensInNewTab ? "_blank" : undefined}
                    rel={opensInNewTab ? "noreferrer" : undefined}
                    style={{ clipPath }}
                    className={`relative hidden flex-1 items-center justify-center gap-3 px-12 py-3 text-white transition hover:brightness-110 min-[870px]:flex ${item.style} -ml-10 first:ml-0`}
                  >
                    <Icon className="h-7 w-7 flex-none" aria-hidden="true" />
                    <div className="flex flex-col">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] opacity-80">
                        {item.info}
                      </p>
                      <p className="text-[13px] font-bold uppercase tracking-[0.1em]">
                        {item.action}
                      </p>
                    </div>
                  </a>
                );
              })}
            </div>
          </div>
        </div>

        <nav ref={mainNavRef} className="border-t border-white/10">
          <div className="mx-auto w-full max-w-6xl px-6 lg:px-10">
            <div className="flex items-center justify-between min-[870px]:hidden">
              <a
                href="#hero"
                aria-label="Nach oben"
                onClick={handleHomeClick}
                className={`group relative flex items-center justify-center px-1 py-5 transition ${
                  activeHref === "#hero" ? "text-white" : "text-brand-100 hover:text-white"
                }`}
              >
                <HomeIcon className="h-5 w-5" aria-hidden="true" />
                <span
                  className={`absolute inset-x-0 bottom-0 h-0.5 bg-brand-500 transition-opacity ${
                    activeHref === "#hero" ? "opacity-100" : "opacity-0 group-hover:opacity-70"
                  }`}
                />
              </a>

              <button
                type="button"
                onClick={() => {
                  setStickyMobileNavOpen(false);
                  setMainMobileNavOpen((prev) => !prev);
                }}
                aria-expanded={mainMobileNavOpen}
                aria-controls="main-mobile-nav"
                aria-label="Navigation umschalten"
                className="rounded-md border border-white/20 p-2 text-brand-100 transition hover:border-brand-500 hover:text-white"
              >
                {mainMobileNavOpen ? <XMarkIcon className="h-5 w-5" /> : <Bars3Icon className="h-5 w-5" />}
              </button>
            </div>

            <div
              id="main-mobile-nav"
              className={`${mainMobileNavOpen ? "grid" : "hidden"} gap-2 pb-4 pt-4 min-[870px]:hidden`}
            >
              {navigation.map((item, index) => (
                <a
                  key={`main-mobile-${item.label}`}
                  href={item.href}
                  onClick={() => {
                    setActiveHref(item.href);
                    setMainMobileNavOpen(false);
                  }}
                  className={`group relative rounded-md px-4 py-3 text-sm font-semibold uppercase tracking-[0.12em] transition ${
                    index === 0 ? "mt-2" : ""
                  } ${
                    activeHref === item.href ? "text-white" : "text-brand-100 hover:text-white"
                  }`}
                >
                  {item.label}
                  <span
                    className={`absolute bottom-0 left-3 h-0.5 w-1/2 bg-brand-500 transition-opacity ${
                      activeHref === item.href ? "opacity-100" : "opacity-0 group-hover:opacity-70"
                    }`}
                  />
                </a>
              ))}
            </div>

            <div className="hidden items-end gap-8 overflow-x-auto pl-4 min-[870px]:flex">
              <a
                href="#hero"
                aria-label="Nach oben"
                onClick={handleHomeClick}
                className={`group relative flex items-center justify-center px-1 py-5 transition ${
                  activeHref === "#hero" ? "text-white" : "text-brand-100 hover:text-white"
                }`}
              >
                <HomeIcon className="h-5 w-5" aria-hidden="true" />
                <span
                  className={`absolute inset-x-0 bottom-0 h-0.5 bg-brand-500 transition-opacity ${
                    activeHref === "#hero" ? "opacity-100" : "opacity-0 group-hover:opacity-70"
                  }`}
                />
              </a>
              {navigation.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  onClick={() => setActiveHref(item.href)}
                  className={`group relative whitespace-nowrap px-1 py-5 text-sm font-semibold uppercase tracking-[0.12em] transition ${
                    activeHref === item.href ? "text-white" : "text-brand-100 hover:text-white"
                  }`}
                >
                  {item.label}
                  <span
                    className={`absolute inset-x-0 bottom-0 h-0.5 bg-brand-500 transition-opacity ${
                      activeHref === item.href ? "opacity-100" : "opacity-0 group-hover:opacity-70"
                    }`}
                  />
                </a>
              ))}
            </div>
          </div>
        </nav>
      </header>

      <main>
        <section
          id="hero"
          className="relative flex items-center overflow-hidden"
          style={{ minHeight: `calc(100svh - ${headerHeight}px)` }}
        >
          <img
            src="/images/hero.png"
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 h-full w-full object-cover object-[62%_28%] lg:object-[64%_24%]"
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-brand-950/95 via-brand-900/78 to-brand-900/34" />

          <div className="relative mx-auto w-full max-w-6xl px-6 py-14 sm:py-18 lg:px-10 lg:py-20">
            <p className="mb-7 inline-block rounded-2xl border border-brand-500/55 bg-black/30 px-6 py-2 text-xs font-semibold uppercase tracking-[0.26em] text-brand-300">
              Create · Develop · Improve
            </p>
            <h1 className="max-w-4xl font-display text-5xl font-semibold leading-[1.02] text-white sm:text-6xl lg:text-7xl">
              Skalierbare <span className="text-brand-400">IT.</span>
              <br />
              Klare <span className="text-brand-400">Architektur.</span>
              <br />
              Starke <span className="text-brand-400">Organisation.</span>
            </h1>
            <p className="mt-8 max-w-3xl text-lg leading-relaxed text-brand-100/92 sm:text-xl lg:text-2xl">
              Management-Beratung, Leadership und technische Tiefe — für klare Entscheidungen, starke Teams und verlässliche Umsetzung.
            </p>
            <div className="mt-11 flex flex-wrap gap-4">
              <a
                href="#kontakt"
                className="group inline-flex items-center gap-3 rounded-full bg-brand-400 px-8 py-4 text-base font-semibold text-white transition hover:bg-brand-500"
              >
                Erstgespräch vereinbaren
                <ArrowRightIcon
                  className="h-5 w-5 transition-transform group-hover:translate-x-1"
                  aria-hidden="true"
                />
              </a>
              <a
                href="#leistungen"
                className="group inline-flex items-center gap-3 rounded-full border border-white/35 bg-black/18 px-8 py-4 text-base font-semibold text-white transition hover:border-brand-300 hover:text-brand-200"
              >
                Leistungen ansehen
                <ArrowRightIcon
                  className="h-5 w-5 text-brand-400 transition-transform group-hover:translate-x-1"
                  aria-hidden="true"
                />
              </a>
            </div>
          </div>
        </section>

        <section id="leistungen" className="text-brand-50 lg:flex lg:min-h-screen lg:flex-col">
          <div className="bg-[#edf0f3] py-3 text-brand-900 sm:py-4">
            <div ref={serviceStatsRef} className="mx-auto max-w-4xl px-3 sm:px-4">
              <div className="grid grid-cols-3 gap-0">
                {serviceAnchorStats.map((item, index) => {
                  const Icon = item.icon;
                  const animatedValue = animatedStatValues[index] ?? 0;
                  const displayValue = index === 2 ? animatedValue.toLocaleString("de-DE") : String(animatedValue);
                  return (
                    <article
                      key={item.label}
                      className={`px-2 text-center ${
                        index < serviceAnchorStats.length - 1 ? "border-r border-slate-300/80" : ""
                      }`}
                    >
                      <span className="mx-auto inline-flex h-6 w-6 items-center justify-center rounded-full border border-brand-300/70 text-brand-400 sm:h-8 sm:w-8">
                        <Icon className="h-3.5 w-3.5 sm:h-4.5 sm:w-4.5" aria-hidden="true" />
                      </span>
                      <span className="mx-auto mt-1 block h-0.5 w-[72%] max-w-[120px] bg-brand-300/70" />
                      <span className="mx-auto -mt-1 block h-1.5 w-1.5 rounded-full bg-brand-400" />

                      <p className="mt-1 font-display text-2xl font-semibold leading-none text-brand-400 sm:text-3xl">{displayValue}</p>
                      <p className="mx-auto mt-1 max-w-[7rem] text-[10px] font-semibold leading-tight text-slate-700 sm:max-w-[8rem] sm:text-xs">
                        {item.label}
                      </p>
                    </article>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="relative overflow-hidden py-20 lg:flex lg:flex-1 lg:items-center lg:py-24">
            <img
              src="/images/hamburg.png"
              alt=""
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 top-0 h-[1800px] w-full object-cover object-top"
            />
            <div className="pointer-events-none absolute inset-0 bg-brand-950/78" />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-brand-950/88 via-brand-950/72 to-brand-950/48" />

            <div className="relative mx-auto max-w-[1500px] px-6 lg:px-10 xl:px-12">
              <div className="flex items-center gap-3">
                <span className="h-0.5 w-14 bg-brand-400/90" />
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-400">Leistungen</p>
              </div>
              <h2 className="mt-3 max-w-5xl font-display text-4xl font-semibold leading-tight text-white sm:text-5xl">
                Vier Hebel für <span className="text-brand-400">stabile Technik</span>,
                <br />
                <span className="text-brand-400">starke Organisation</span> und <span className="text-brand-400">planbare Umsetzung</span>.
              </h2>
              <p className="mt-6 max-w-4xl text-xl leading-relaxed text-brand-100/90">
                Interim Leadership, IT-Beratung und Softwarearchitektur — für klare Prioritäten, bessere Entscheidungen und verlässliche Ergebnisse.
              </p>

              <div className="mt-10 space-y-3">
                {services.map((service, index) => {
                  const Icon = serviceIcons[index] ?? CodeBracketIcon;
                  const isOpen = openServiceIndex === index;

                  return (
                    <article
                      key={service.title}
                      className="rounded-2xl border border-brand-800/70 bg-brand-950/75 p-5 shadow-xl shadow-black/35 backdrop-blur-[1px] sm:p-6"
                    >
                      <button
                        type="button"
                        onClick={() => setOpenServiceIndex(index)}
                        className="flex w-full items-start justify-between gap-4 text-left"
                        aria-expanded={isOpen}
                      >
                        <div className="flex min-w-0 items-start gap-4">
                          <span className="mt-0.5 inline-flex h-12 w-12 flex-none items-center justify-center rounded-full border border-brand-400/55 bg-transparent text-brand-400 sm:h-14 sm:w-14">
                            <Icon className="h-6 w-6 sm:h-7 sm:w-7" aria-hidden="true" />
                          </span>
                          <div>
                            <h3 className="font-display text-2xl font-semibold text-white sm:text-3xl">{service.title}</h3>
                            <p className="mt-3 text-base leading-relaxed text-brand-300/95 sm:text-lg">{service.description}</p>
                          </div>
                        </div>
                          <span className="mt-1 inline-flex h-8 w-8 flex-none items-center justify-center rounded-full border border-brand-400/55 bg-transparent text-brand-400">
                          <ChevronDownIcon
                            className={`h-4.5 w-4.5 transition-transform duration-300 ease-out ${isOpen ? "rotate-180" : "rotate-0"}`}
                            aria-hidden="true"
                          />
                        </span>
                      </button>

                      <div
                        className={`overflow-hidden transition-[max-height,opacity,margin] duration-500 ease-in-out ${
                          isOpen ? "mt-5 opacity-100" : "mt-0 opacity-0"
                        }`}
                        style={{ maxHeight: isOpen ? `${(serviceContentHeights[index] ?? 0) + 24}px` : "0px" }}
                      >
                        <div ref={(element) => {
                          serviceContentRefs.current[index] = element;
                        }}>
                          <span className="mb-4 block h-0.5 w-14 bg-brand-400/85" />

                          <ul className="space-y-2 text-sm leading-relaxed text-brand-100/90 sm:text-base">
                            {service.points.map((point) => (
                              <li key={point} className="flex gap-3">
                                <span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-brand-400" />
                                <span>{point}</span>
                              </li>
                            ))}
                          </ul>

                          <div className="pt-6">
                            <div className="flex items-center gap-2 text-brand-400">
                              <span className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-brand-400/55 bg-transparent text-brand-400">
                                <CheckBadgeIcon className="h-4.5 w-4.5" aria-hidden="true" />
                              </span>
                              <span className="text-sm font-semibold uppercase tracking-[0.08em]">Ergebnis</span>
                              <span className="h-px flex-1 bg-brand-400/40" />
                            </div>
                            <div className="mt-2 rounded-xl border border-brand-400/50 bg-brand-950/35 px-4 py-3">
                              <p className="text-base leading-relaxed text-brand-100/95">{service.outcome}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        <section className="text-brand-50 lg:flex lg:min-h-screen lg:flex-col">
          <div id="kunden" className="scroll-mt-0 bg-[#edf0f3] py-3 text-brand-900 sm:py-4 lg:scroll-mt-0">
            <div className="mx-auto max-w-none overflow-hidden px-0">
              <div
                ref={customerSliderRef}
                className="flex w-full gap-10 overflow-x-auto px-2 py-2 sm:px-4 lg:px-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              >
                {[...customerLogos, ...customerLogos].map((logo, index) => (
                  <article
                    key={`${logo.src}-${index}`}
                    className="min-w-[46vw] px-2 py-2 sm:min-w-[34vw] md:min-w-[28vw] lg:min-w-[22vw] xl:min-w-[18vw] 2xl:min-w-[15vw]"
                  >
                    <div className="flex h-28 items-center justify-center">
                      <img
                        src={logo.src}
                        alt={logo.alt}
                        loading="lazy"
                        className="max-h-24 w-auto object-contain"
                      />
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>

          <div className="relative overflow-hidden py-20 lg:flex lg:flex-1 lg:items-center lg:py-24">
            <img
              src="/images/kunden.png"
              alt=""
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 h-full w-full object-cover object-[25%_34%] md:object-[74%_34%] lg:object-[72%_30%] xl:object-[72%_24%] 2xl:object-[72%_20%]"
            />
            <div className="pointer-events-none absolute inset-0 bg-[#030916]/72" />
            <div className="pointer-events-none absolute inset-0 opacity-35 [background-image:linear-gradient(rgba(255,255,255,0.045)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.045)_1px,transparent_1px)] [background-size:22px_22px]" />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-brand-950/78 via-brand-950/66 to-brand-950/56" />

            <div className="relative mx-auto max-w-[1500px] px-6 lg:px-10 xl:px-12">
              <div className="flex items-center gap-3">
                <span className="h-0.5 w-14 bg-brand-400/90" />
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-400">Für wen ich arbeite</p>
              </div>

              <h2 className="mt-4 max-w-5xl font-display text-[clamp(2rem,9vw,3.75rem)] font-semibold leading-[1.08] text-white">
                Für Unternehmen mit
                <br />
                <span className="text-brand-400">wachsender IT-Komplexität</span>
                <br />
                und <span className="text-brand-400">hohem Lieferanspruch</span>.
              </h2>

              <p className="mt-6 max-w-4xl text-2xl leading-relaxed text-brand-100/88">
                Besonders dort, wo Systeme, Teams und Prioritäten komplex werden und Entscheidungen schneller zu
                belastbaren Ergebnissen führen müssen.
              </p>

              <div className="mt-12 grid max-w-5xl gap-4 md:grid-cols-2 xl:grid-cols-3">
                {targetGroups.map((group, index) => {
                  const Icon = targetGroupIcons[index % targetGroupIcons.length];
                  return (
                    <article
                      key={group}
                      className="rounded-2xl border border-brand-800/70 bg-brand-950/75 p-4 shadow-xl shadow-black/35 backdrop-blur-[1px] sm:p-5"
                    >
                      <span className="inline-flex h-14 w-14 items-center justify-center rounded-full border border-brand-400/65 bg-transparent text-brand-400">
                        <Icon className="h-7 w-7" aria-hidden="true" />
                      </span>
                      <p className="mt-5 text-[22px] leading-snug text-brand-100/95">{group}</p>
                      <span className="mt-5 block h-0.5 w-12 bg-brand-400/90" />
                    </article>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        <section id="arbeitsweise" className="relative overflow-hidden bg-[#eef1f3] py-20 text-brand-900 lg:flex lg:min-h-screen lg:items-center lg:py-24">
          <img
            src="/images/arbeitsweise.png"
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute right-0 top-0 h-[88%] w-auto max-w-none object-contain object-right-top opacity-90"
          />
          <div className="pointer-events-none absolute inset-0 bg-[#eef1f3]/90" />
          <div className="relative z-10 mx-auto w-full max-w-[1420px] px-4 sm:px-6 lg:px-8 xl:px-10">
            <div className="lg:pt-1 xl:pt-4">
              <div className="max-w-none">
                <div className="flex items-center gap-3">
                  <span className="h-0.5 w-8 bg-brand-500/70" />
                  <p className="section-kicker">Arbeitsweise</p>
                </div>

                <h2 className="mt-3 max-w-4xl font-display text-[clamp(1.85rem,8vw,3.5rem)] font-semibold leading-[1.1] tracking-[-0.025em] text-brand-900">
                  <span className="text-brand-500">Pragmatisch</span>, klar und
                  <br />
                  <span className="text-brand-500">umsetzungsorientiert.</span>
                  <br />
                  Keine PowerPoint-Beratung.
                </h2>

                <div className="mt-8">
                  <div className="flex items-center gap-4">
                    <span style={workIconCircleStyle} className="inline-flex h-12 w-12 items-center justify-center rounded-full text-brand-600">
                      <CheckBadgeIcon className="h-6 w-6" aria-hidden="true" />
                    </span>
                    <p className="section-kicker text-brand-600">Prinzipien</p>
                    <span className="h-px flex-1 bg-brand-300/80" />
                  </div>
                  <p className="mt-4 max-w-4xl text-[20px] leading-[1.35] text-brand-900 lg:text-[22px]">
                    Ich arbeite eng mit Management und Teams zusammen,
                    <br />
                    um Klarheit zu schaffen und Entscheidungen wirksam in die Umsetzung zu bringen.
                  </p>

                  <div className="mt-7 grid grid-cols-2 border-y border-brand-200/70 md:grid-cols-4">
                    {workPrinciples.map((item, index) => {
                      const Icon = item.icon;
                      return (
                        <article
                          key={item.title}
                          className={`px-3 py-6 text-center sm:px-4 ${index < workPrinciples.length - 1 ? "md:border-r md:border-brand-200/70" : ""}`}
                        >
                          <span style={workIconCircleStyle} className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full text-brand-600">
                            <Icon className="h-6 w-6" aria-hidden="true" />
                          </span>
                          <p className="mt-4 text-[20px] font-semibold leading-tight text-brand-900 lg:text-[22px]">{item.title}</p>
                          <p className="mt-1 text-[16px] leading-snug text-[#6f757b] lg:text-[17px]">{item.subtitle}</p>
                        </article>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-9">
                  <div className="flex items-center gap-4">
                    <span style={workIconCircleStyle} className="inline-flex h-12 w-12 items-center justify-center rounded-full text-brand-600">
                      <Cog6ToothIcon className="h-6 w-6" aria-hidden="true" />
                    </span>
                    <p className="section-kicker text-brand-600">Vorgehen</p>
                    <span className="h-px flex-1 bg-brand-300/80" />
                  </div>
                  <p className="mt-4 max-w-4xl text-[20px] leading-[1.35] text-brand-900 lg:text-[22px]">
                    Ich priorisiere Wirkung und bringe Struktur in Entscheidungen,
                    <br />
                    Architektur und Umsetzung.
                  </p>

                  <div className="relative mt-6 pl-0 sm:pl-1">
                    <span className="pointer-events-none absolute left-6 top-0 hidden h-full w-px bg-[#d2d7db] sm:block" />

                    <div className="space-y-4">
                      {processSteps.map((item) => {
                        const Icon = item.icon;

                        return (
                          <div key={item.step} className="grid grid-cols-1 items-center gap-4 sm:grid-cols-[auto_1fr] sm:gap-4">
                            <span className="relative z-10 hidden h-10 w-10 items-center justify-center rounded-full bg-brand-500 text-[14px] font-bold tracking-[0.04em] text-white shadow-[0_2px_10px_rgba(79,175,63,0.38)] sm:inline-flex">
                              {item.step}
                            </span>

                            <article className="relative grid grid-cols-1 overflow-hidden rounded-xl border border-[#d8dde2] bg-white shadow-[0_4px_18px_-16px_rgba(16,24,32,0.28)] sm:grid-cols-[minmax(300px,0.9fr)_1px_1.1fr]">
                              <span className="absolute right-4 top-4 inline-flex h-9 min-w-[2.25rem] items-center justify-center rounded-full bg-brand-500 px-2 text-[13px] font-bold tracking-[0.04em] text-white shadow-[0_2px_10px_rgba(79,175,63,0.32)] sm:hidden">
                                {item.step}
                              </span>
                              <div className="flex items-center gap-5 px-6 py-5 sm:px-7">
                                <span style={workIconCircleStyle} className="inline-flex h-16 w-16 items-center justify-center rounded-full text-brand-600">
                                  <Icon className="h-8 w-8" aria-hidden="true" />
                                </span>
                                <div>
                                  <p className="text-[18px] font-semibold leading-tight text-brand-900 lg:text-[20px]">{item.title}</p>
                                  <span className="mt-2 block h-[3px] w-8 rounded-sm bg-brand-500" />
                                </div>
                              </div>
                              <span className="hidden w-px bg-brand-200 sm:block" aria-hidden="true" />
                              <div className="flex items-center border-t border-brand-200 px-5 py-5 sm:border-t-0 sm:px-7">
                                <p className="text-[15px] leading-[1.42] text-[#6f757b] lg:text-[16px]">{item.text}</p>
                              </div>
                            </article>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="erfahrung" className="relative overflow-hidden border-y border-white/10 bg-brand-900 py-24 lg:flex lg:min-h-screen lg:items-center lg:py-32">
          <div className="pointer-events-none absolute inset-0 opacity-35 [background-image:linear-gradient(rgba(55,112,192,0.18)_1px,transparent_1px),linear-gradient(90deg,rgba(55,112,192,0.18)_1px,transparent_1px)] [background-size:24px_24px]" />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-brand-950/88 via-brand-950/80 to-brand-950/74" />

          <div className="relative mx-auto w-full max-w-[1700px] px-6 lg:px-10 xl:px-12">
            <div className="flex items-center gap-3">
              <span className="h-0.5 w-14 bg-brand-400/90" />
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-400">Erfahrung & Kompetenzfelder</p>
            </div>

            <h2 className="mt-5 max-w-6xl font-display text-[clamp(2.05rem,9.2vw,4rem)] font-semibold leading-[1.08] text-white">
              Interim <span className="text-brand-400">Leadership</span>,
              <br />
              <span className="text-brand-400">Architekturberatung</span>
              <br />
              und <span className="text-brand-400">Umsetzung</span>
              <br />
              aus einer Hand.
            </h2>

            <span className="mt-6 block h-0.5 w-12 bg-brand-400" />

            <p className="mt-7 max-w-5xl text-xl leading-relaxed text-brand-100/92 lg:text-2xl">
              Ich unterstütze Unternehmen dort, wo Technologie, Organisation und Delivery zusammenkommen -
              als Interim CTO/Director Engineering, Architekturberater, Tech Lead oder hands-on Sparringspartner.
            </p>

            <div className="mt-12 grid gap-6 xl:grid-cols-[0.95fr_2.05fr]">
              <article className="flex h-full flex-col rounded-2xl border border-brand-800/70 bg-brand-950/75 p-7 shadow-xl shadow-black/35 backdrop-blur-[1px] lg:p-8">
                <span className="inline-flex h-20 w-20 items-center justify-center rounded-full border border-brand-400/65 bg-transparent text-brand-400">
                  <ArrowRightIcon className="h-10 w-10" aria-hidden="true" />
                </span>
                <h3 className="mt-6 max-w-sm font-display text-3xl font-semibold leading-tight text-white lg:text-4xl">
                  Strategie. Menschen. Technologie.
                  <br />
                  Messbare Wirkung.
                </h3>
                <p className="mt-5 max-w-sm text-lg leading-relaxed text-brand-100/92">
                  Ich übernehme Verantwortung, bringe Klarheit in komplexe Situationen und liefere Ergebnisse.
                </p>

                <div className="mt-8 space-y-6 border-y border-white/10 py-7">
                  {experienceStats.map((item) => {
                    const Icon = item.icon;
                    return (
                      <div key={item.label} className="flex items-start gap-4">
                        <span className="inline-flex h-11 w-11 flex-none items-center justify-center rounded-full border border-brand-400/55 bg-transparent text-brand-400">
                          <Icon className="h-6 w-6" aria-hidden="true" />
                        </span>
                        <div className="flex items-center gap-3">
                          <p className="w-[6.4rem] text-right font-display text-5xl font-semibold leading-[0.96] text-white">{item.value}</p>
                          <p className="max-w-[14rem] text-sm leading-snug text-brand-100/88">{item.label}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-7 flex flex-1 flex-col justify-center gap-8">
                  <p className="text-sm font-semibold uppercase tracking-[0.14em] text-brand-300">Vertrauen aus der Praxis</p>
                  <div className="overflow-hidden px-1">
                    <img
                      src="/images/customers/kompetenz/logos-strip.png"
                      alt="Logos von Porsche Digital, EDEKA, The Quality Group, Fielmann und CHECK24"
                      loading="lazy"
                      className="h-auto w-full object-contain"
                    />
                  </div>
                  <img
                    src="/images/kompetenz.png"
                    alt="Kompetenz und Referenzen"
                    loading="lazy"
                    className="mx-auto h-auto w-4/5 object-contain"
                  />
                </div>
              </article>

              <div className="space-y-6">
                <div className="grid gap-6 xl:grid-cols-3 xl:items-stretch">
                  {experienceRoles.slice(0, 2).map((item) => {
                    const Icon = item.icon;
                    return (
                      <article key={item.title} className="flex h-full min-h-[31rem] flex-col rounded-2xl border border-brand-800/70 bg-brand-950/75 p-7 shadow-xl shadow-black/35 backdrop-blur-[1px] lg:p-8">
                        <div className="flex items-center justify-between">
                          <span className="inline-flex h-16 w-16 items-center justify-center rounded-full border border-brand-400/60 bg-transparent text-brand-400">
                            <Icon className="h-8 w-8" aria-hidden="true" />
                          </span>
                          <span className="text-2xl font-semibold text-brand-400">{item.step}</span>
                        </div>
                        <div className="flex flex-1 flex-col">
                          <h3 className="mt-5 font-display text-[26px] font-semibold leading-tight text-white lg:text-[32px]">{item.title}</h3>
                          <span className="mt-3 block h-0.5 w-12 bg-brand-400/90" />
                          <p className="mt-5 text-lg leading-relaxed text-brand-100/88">{item.summary}</p>
                          <p className="mt-4 text-base leading-relaxed text-brand-100/82">{item.detail}</p>
                          <p className="mt-auto border-t border-white/10 pt-5 text-base leading-relaxed text-brand-100/88">
                            <span className="font-semibold text-brand-400">Ergebnis:</span> {item.result}
                          </p>
                        </div>
                      </article>
                    );
                  })}

                  <article className="flex h-full min-h-[31rem] overflow-hidden rounded-2xl border border-brand-800/70 bg-brand-950/75 shadow-xl shadow-black/35 backdrop-blur-[1px]">
                    <img
                      src="/images/backlog.png"
                      alt="Moderation am Board"
                      loading="lazy"
                      className="h-full w-full object-cover"
                    />
                  </article>
                </div>

                <div className="grid gap-6 xl:grid-cols-3 xl:items-stretch">
                  {experienceRoles.slice(2).map((item) => {
                    const Icon = item.icon;
                    return (
                      <article key={item.title} className="flex h-full min-h-[31rem] flex-col rounded-2xl border border-brand-800/70 bg-brand-950/75 p-7 shadow-xl shadow-black/35 backdrop-blur-[1px] lg:p-8">
                        <div className="flex items-center justify-between">
                          <span className="inline-flex h-16 w-16 items-center justify-center rounded-full border border-brand-400/60 bg-transparent text-brand-400">
                            <Icon className="h-8 w-8" aria-hidden="true" />
                          </span>
                          <span className="text-2xl font-semibold text-brand-400">{item.step}</span>
                        </div>
                        <div className="flex flex-1 flex-col">
                          <h3 className="mt-5 font-display text-[26px] font-semibold leading-tight text-white lg:text-[32px]">{item.title}</h3>
                          <span className="mt-3 block h-0.5 w-12 bg-brand-400/90" />
                          <p className="mt-5 text-lg leading-relaxed text-brand-100/88">{item.summary}</p>
                          <p className="mt-4 text-base leading-relaxed text-brand-100/82">{item.detail}</p>
                          <p className="mt-auto border-t border-white/10 pt-5 text-base leading-relaxed text-brand-100/88">
                            <span className="font-semibold text-brand-400">Ergebnis:</span> {item.result}
                          </p>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="mt-10 rounded-2xl border border-brand-800/70 bg-brand-950/70 px-6 py-5 shadow-xl shadow-black/35 backdrop-blur-[1px]">
              <div className="grid gap-5 md:grid-cols-4 md:items-center md:gap-0">
                {experienceValueStrip.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={item.text}
                      className={`flex items-center gap-3 md:justify-center md:px-6 ${index > 0 ? "md:border-l md:border-white/10" : ""}`}
                    >
                      <span className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-brand-400/55 bg-transparent text-brand-400">
                        <Icon className="h-5 w-5" aria-hidden="true" />
                      </span>
                      <p className="whitespace-pre-line text-base leading-snug text-brand-100/92">{item.text}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        <section id="kontakt" className="relative overflow-hidden bg-[#f5f7f4] py-20 text-brand-900 lg:flex lg:min-h-screen lg:items-center lg:py-0">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(79,175,63,0.09),transparent_35%),radial-gradient(circle_at_82%_30%,rgba(79,175,63,0.07),transparent_28%),linear-gradient(180deg,rgba(255,255,255,0.88),rgba(245,247,244,0.96))]" />

          <div className="relative mx-auto grid w-full max-w-7xl items-center gap-12 px-6 lg:grid-cols-[0.92fr_1.08fr] lg:px-10 xl:gap-16">
            <div className="max-w-2xl">
              <div className="flex items-center gap-3">
                <span className="h-0.5 w-8 bg-brand-500/70" />
                <p className="section-kicker">Kontakt</p>
              </div>

              <h2 className="mt-4 max-w-[31rem] font-display text-[clamp(1.65rem,2.95vw,2.35rem)] font-semibold leading-[1.0] tracking-[-0.03em] text-brand-900">
                Lass uns sprechen,
                <br />
                wenn Technik, Team und
                <br />
                Delivery wieder klarer
                <br />
                werden sollen.
              </h2>

              <span className="mt-6 block h-0.5 w-16 bg-brand-500" />

              <p className="mt-6 max-w-2xl text-lg leading-relaxed text-gray-500 sm:text-xl">
                In einem kurzen Erstgespräch klären wir Ausgangslage, Zielbild und konkrete nächste Schritte.
              </p>

              <div className="mt-10 flex flex-wrap gap-4">
                <a
                  href="https://calendly.com/sean-roddis/coffee-let-s-talk-about-the-challenges-you-re-facing?month=2026-05&date=2026-05-29"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-500 px-7 py-3.5 text-sm font-semibold text-white transition hover:bg-brand-400"
                >
                  <CalendarDaysIcon className="h-4 w-4 flex-none text-white" aria-hidden="true" />
                  Erstgespräch vereinbaren
                </a>
                <a
                  href="tel:+491724225545"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-brand-300 bg-white px-7 py-3.5 text-sm font-semibold text-brand-700 transition hover:border-brand-500 hover:text-brand-900"
                >
                  <PhoneIcon className="h-4 w-4 flex-none text-brand-500" aria-hidden="true" />
                  Projekt besprechen
                </a>
              </div>

              <div className="mt-8 flex items-center gap-3 text-sm text-gray-500">
                <CheckBadgeIcon className="h-5 w-5 flex-none text-brand-500" aria-hidden="true" />
                <span>Unkomplizierter Erstkontakt. Klar. Pragmatisch.</span>
              </div>
            </div>

            <div className="mx-auto w-full max-w-[520px]">
              <div className="mx-auto aspect-square w-full overflow-hidden rounded-[42%_58%_63%_37%/45%_35%_65%_55%] shadow-[0_20px_42px_-30px_rgba(16,24,32,0.38)]">
                <img
                  src="/images/home-portray.jpg"
                  alt="Sean Roddis"
                  className="h-full w-full object-cover object-center"
                />
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/10 bg-black text-brand-100">
        <div className="mx-auto grid w-full max-w-7xl gap-8 px-6 py-10 lg:grid-cols-[1fr_1.25fr_1.35fr] lg:px-10">
          <div className="lg:pr-8">
            <img
              src="/images/roddis_logo.svg"
              alt="Roddis Consulting Logo"
              className="h-14 w-auto object-contain"
            />
            <p className="mt-5 max-w-xs text-[18px] leading-snug text-brand-100 lg:text-[20px]">
              Roddis Consulting - Interim Leadership, Architekturberatung und Umsetzung für
              skalierende IT-Organisationen.
            </p>
            <div className="mt-7 flex gap-3">
              {socialLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={link.label}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-brand-500/50 bg-brand-900/40 text-xs font-bold uppercase tracking-wide text-brand-100 transition hover:border-brand-500 hover:text-brand-300"
                >
                  {link.icon}
                </a>
              ))}
            </div>
          </div>

          <div className="border-white/10 lg:border-l lg:pl-8">
            <span className="mb-4 block h-0.5 w-10 bg-brand-500" />
            <h3 className="text-xl font-bold uppercase tracking-tight text-white lg:text-2xl">Kontakt Info</h3>
            <div className="mt-4 space-y-2 text-base text-brand-200 lg:text-lg">
              <a
                href="tel:+491724225545"
                className="group flex items-center gap-3 border-b border-white/10 py-3 transition hover:text-brand-300"
              >
                <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full border border-brand-500/50 bg-black/35">
                  <PhoneIcon className="h-5 w-5 text-brand-500" aria-hidden="true" />
                </span>
                <span className="relative inline-flex flex-col">
                  <span>+49 172 422 55 45</span>
                  <span className="mt-1 h-1 w-0 rounded-full bg-brand-500 transition-all duration-300 group-hover:w-full" />
                </span>
              </a>
              <a
                href="mailto:sean.roddis@roddis-consulting.com"
                className="group flex items-center gap-3 border-b border-white/10 py-3 transition hover:text-brand-300"
              >
                <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full border border-brand-500/50 bg-black/35">
                  <EnvelopeIcon className="h-5 w-5 text-brand-500" aria-hidden="true" />
                </span>
                <span className="relative inline-flex flex-col break-words">
                  <span>sean.roddis@roddis-consulting.com</span>
                  <span className="mt-1 h-1 w-0 rounded-full bg-brand-500 transition-all duration-300 group-hover:w-full" />
                </span>
              </a>
              <div className="group flex items-center gap-3 py-3">
                <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full border border-brand-500/50 bg-black/35">
                  <MapPinIcon className="h-5 w-5 text-brand-500" aria-hidden="true" />
                </span>
                <span className="relative inline-flex flex-col">
                  <span>Hamburg, Deutschland</span>
                  <span className="mt-1 h-1 w-0 rounded-full bg-brand-500 transition-all duration-300 group-hover:w-full" />
                </span>
              </div>
            </div>
          </div>

          <div className="border-white/10 lg:border-l lg:pl-8">
            <span className="mb-4 block h-0.5 w-10 bg-brand-500" />
            <h3 className="text-xl font-bold uppercase tracking-tight text-white lg:text-2xl">Standort</h3>
            <div className="relative mt-4 w-full aspect-[297/159] overflow-hidden rounded-2xl border border-white/10 bg-brand-900">
              <img
                src="/images/map.png"
                alt="Standortkarte"
                className="h-full w-full object-cover object-[50%_45%] opacity-85"
              />
              <div className="absolute left-[52.9%] top-[27.8%] -translate-x-1/2 -translate-y-1/2">
                <span className="absolute inset-[-2px] h-5 w-5 animate-ping rounded-full bg-[#7CFC00]/95" />
                <span className="absolute inset-[-7px] h-8 w-8 animate-pulse rounded-full border-2 border-[#B6FF4D]/95" />
                <span className="relative block h-4 w-4 rounded-full bg-[#39FF14] shadow-[0_0_0_7px_rgba(57,255,20,0.48),0_0_26px_7px_rgba(124,252,0,0.72)]" />
              </div>
              <div className="absolute bottom-3 left-3 flex items-center gap-1.5 rounded-full bg-black/70 px-3 py-1 text-xs font-semibold text-brand-100 shadow-sm">
                <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4 text-brand-500 fill-current">
                  <path d="M12 2.5C8.7 2.5 6 5.2 6 8.5C6 13.4 12 21.5 12 21.5C12 21.5 18 13.4 18 8.5C18 5.2 15.3 2.5 12 2.5ZM12 11.1C10.6 11.1 9.4 9.9 9.4 8.5C9.4 7.1 10.6 5.9 12 5.9C13.4 5.9 14.6 7.1 14.6 8.5C14.6 9.9 13.4 11.1 12 11.1Z" />
                </svg>
                <span>Hamburg</span>
              </div>
            </div>
          </div>
        </div>
      </footer>

      <div className="border-t border-white/10 bg-brand-900/95 text-brand-100 backdrop-blur">
        <div className="mx-auto flex w-full max-w-7xl flex-col items-center gap-3 px-6 py-4 text-sm lg:px-10">
          <a
            href="/#/impressum"
            className="inline-flex w-fit items-center text-brand-300 transition hover:text-white"
          >
            Impressum
          </a>
          <p className="text-brand-200">Roddis Consulting © {currentYear}. All Rights Reserved</p>
        </div>
      </div>

      <button
        type="button"
        aria-label="Nach oben scrollen"
        onClick={scrollToTop}
        className={`group fixed bottom-[calc(env(safe-area-inset-bottom)+1.25rem)] right-[calc(env(safe-area-inset-right)+1.25rem)] z-[60] flex h-14 w-14 items-center justify-center rounded-full p-[3px] text-white shadow-lg shadow-black/35 transition-[transform,opacity] duration-300 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${
          showScrollTop ? "pointer-events-auto translate-y-0 opacity-100" : "pointer-events-none translate-y-8 opacity-0"
        }`}
      >
        <span
          className="pointer-events-none absolute inset-0 rounded-full"
          style={{
            background: `conic-gradient(rgba(79,175,63,0.96) ${scrollProgress * 360}deg, rgba(255,255,255,0.18) ${scrollProgress * 360}deg)`,
          }}
        />
        <span className="pointer-events-none absolute inset-[3px] rounded-full bg-brand-800 transition group-hover:bg-brand-700" />
        <ArrowUpIcon className="relative h-6 w-6" aria-hidden="true" />
      </button>
    </div>
  );
}

export default App;
